/**
 * Canvas renderer: paints a Label Document Model onto a 2D context.
 *
 * Ported from ns-web resources/js/components/common/label-viewer/utils/canvas-renderer.js
 * Changes from NS:
 *  - Draws in printer-dot space under one ctx.scale(), instead of rounding every
 *    coordinate to pixels separately (which drifted by up to a pixel per element).
 *  - Draws exactly one label, clipped to the print width and label length as
 *    the printer does. Overflow is reported in the result (see content-extent.ts)
 *    instead of being drawn as extra pages.
 *  - Text wraps through layoutText(), shared with measureContent(), so the two
 *    can't disagree about line breaks or line height.
 *  - Field rotation (^A / ^FW / ^BC orientation) rotates inside the field's box
 *    anchored at ^FO, as the printer does; NS rotated around the ^FO point,
 *    which put R/I/B fields above or left of their origin.
 *  - ^FR and ^LR use 'difference' compositing per element, so reversed fields
 *    turn black to white and white to black where they overlap.
 *  - ^GB borders are drawn inside the box, filled when the border covers it.
 *  - Doesn't resize or style the canvas; the caller sizes it (canvasSizePx()).
 *  - No DOM access (fonts.ready, createElement); the caller awaits fonts.
 */

import type {
  BarcodeElement,
  BoxElement,
  CircleElement,
  DiagonalElement,
  GraphicElement,
  LabelDoc,
  LabelElement,
  Rotation,
  TextElement,
} from './document-model';
import type { LabelCanvasContext } from './canvas-context';
import { baselineOffsetDots, bitmapCellWidthDots, cssFontForDots, font0WidthFactor, isBitmapFont } from './font-mapper';
import { type MeasureText, layoutText } from './text-layout';
import {
  drawBarcodePlaceholder,
  drawEncoded,
  encodeBarcode,
  encodedSize,
  loadBarcodeEncoder,
  type BarcodeEncoder,
} from './barcode-renderer';
import { type ContentExtent, type LabelGeometry, describeClipping, labelSizeDots, measureContent } from './content-extent';
import { printsEmptySymbol } from './barcode-types';
import { type EmptyBarcode, emptyBarcodeWarning, findEmptyBarcodes } from './annotations';
import { pxPerDot } from './unit-converter';

export interface RenderOptions extends LabelGeometry {
  /** 1 = physical size on a 96-dpi CSS display. Multiply by devicePixelRatio for crisp output. */
  zoom?: number;
  /**
   * Extra horizontal scale for scalable-font text (1 = none). A calibration
   * knob for matching a specific printer, like NS's canvasFontAdjZpl.
   */
  fontWidthAdjust?: number;
}

export interface RenderResult {
  /** Canvas size the label occupies, in pixels. */
  widthPx: number;
  heightPx: number;
  /** Where content landed in dots, and what the printer will clip. */
  extent: ContentExtent;
  /** Differences between this preview and what the printer will produce. */
  warnings: string[];
  /** Barcodes with empty data (for preview-only annotations; see renderAnnotations). */
  emptyBarcodes: EmptyBarcode[];
}

const BLACK = '#000000';
const WHITE = '#ffffff';

/** Canvas buffer size in pixels for a label at a zoom level. */
export function canvasSizePx(options: RenderOptions): { width: number; height: number } {
  const size = labelSizeDots(options);
  const scale = pxPerDot(options.dpi, options.zoom ?? 1);
  return { width: Math.round(size.width * scale), height: Math.round(size.height * scale) };
}

/** Text measurement in dots using the context's real font metrics. */
export function canvasMeasure(ctx: LabelCanvasContext, fontWidthAdjust = 1): MeasureText {
  return (text, font) => {
    const cell = bitmapCellWidthDots(font);
    if (cell !== null) return text.length * cell;
    ctx.font = cssFontForDots(font);
    return ctx.measureText(text).width * font0WidthFactor(font) * fontWidthAdjust;
  };
}

/**
 * Transform so (0, 0) is the top-left of a field box of size w × h (unrotated
 * content size) placed at x, y with the given rotation.
 */
function enterField(ctx: LabelCanvasContext, x: number, y: number, w: number, h: number, rotate: Rotation): void {
  ctx.translate(x, y);
  if (rotate === 90) {
    ctx.translate(h, 0);
    ctx.rotate(Math.PI / 2);
  } else if (rotate === 180) {
    ctx.translate(w, h);
    ctx.rotate(Math.PI);
  } else if (rotate === 270) {
    ctx.translate(0, w);
    ctx.rotate(-Math.PI / 2);
  }
}

function setInk(ctx: LabelCanvasContext, color: 'B' | 'W', reverse: boolean): string {
  if (reverse) {
    ctx.globalCompositeOperation = 'difference';
    return WHITE;
  }
  ctx.globalCompositeOperation = 'source-over';
  return color === 'W' ? WHITE : BLACK;
}

function drawText(ctx: LabelCanvasContext, el: TextElement, measure: MeasureText, adjust: number, reverse: boolean): void {
  if (!el.text) return;
  const layout = layoutText(el, measure);
  ctx.save();
  ctx.fillStyle = setInk(ctx, 'B', reverse);
  enterField(ctx, el.x, el.y, layout.width, layout.height, el.rotate);
  ctx.font = cssFontForDots(el.font);
  // Baseline at cap height below the line top, so capitals start at ^FO y as printed.
  ctx.textBaseline = 'alphabetic';
  ctx.textAlign = 'left';
  const baseline = baselineOffsetDots(el.font);
  const cell = bitmapCellWidthDots(el.font);
  for (const line of layout.lines) {
    if (!line.text) continue;
    ctx.save();
    ctx.translate(line.x - el.x, line.y - el.y);
    // Stretch the CSS glyphs to the width the layout assumed, so what's drawn
    // matches the wrap decisions (fixed cells for bitmap fonts, w/h for font 0).
    const natural = ctx.measureText(line.text).width;
    const target = cell !== null ? line.text.length * cell : natural * font0WidthFactor(el.font) * adjust;
    if (natural > 0 && Math.abs(target - natural) > 0.01) ctx.scale(target / natural, 1);
    ctx.fillText(line.text, 0, baseline);
    ctx.restore();
  }
  ctx.restore();
}

function drawBox(ctx: LabelCanvasContext, el: BoxElement, reverse: boolean): void {
  ctx.save();
  ctx.fillStyle = setInk(ctx, el.color, reverse);
  const t = el.thickness;
  const solid = t * 2 >= Math.min(el.w, el.h);
  const radius = (el.rounding / 8) * (Math.min(el.w, el.h) / 2);
  if (radius <= 0) {
    if (solid) {
      ctx.fillRect(el.x, el.y, el.w, el.h);
    } else {
      ctx.fillRect(el.x, el.y, el.w, t);
      ctx.fillRect(el.x, el.y + el.h - t, el.w, t);
      ctx.fillRect(el.x, el.y + t, t, el.h - 2 * t);
      ctx.fillRect(el.x + el.w - t, el.y + t, t, el.h - 2 * t);
    }
  } else {
    // Rounded border: outer rounded rect minus inner (even-odd fill).
    ctx.beginPath();
    roundedRectPath(ctx, el.x, el.y, el.w, el.h, radius);
    if (!solid) roundedRectPath(ctx, el.x + t, el.y + t, el.w - 2 * t, el.h - 2 * t, Math.max(0, radius - t));
    ctx.fill('evenodd');
  }
  ctx.restore();
}

function roundedRectPath(ctx: LabelCanvasContext, x: number, y: number, w: number, h: number, r: number): void {
  ctx.moveTo(x + r, y);
  ctx.arcTo(x + w, y, x + w, y + h, r);
  ctx.arcTo(x + w, y + h, x, y + h, r);
  ctx.arcTo(x, y + h, x, y, r);
  ctx.arcTo(x, y, x + w, y, r);
  ctx.closePath();
}

function drawCircle(ctx: LabelCanvasContext, el: CircleElement, reverse: boolean): void {
  ctx.save();
  ctx.fillStyle = setInk(ctx, el.color, reverse);
  const r = el.diameter / 2;
  ctx.beginPath();
  ctx.arc(el.x + r, el.y + r, r, 0, Math.PI * 2);
  if (el.thickness < r) ctx.arc(el.x + r, el.y + r, r - el.thickness, 0, Math.PI * 2, true);
  ctx.fill('evenodd');
  ctx.restore();
}

function drawDiagonal(ctx: LabelCanvasContext, el: DiagonalElement, reverse: boolean): void {
  ctx.save();
  ctx.fillStyle = setInk(ctx, el.color, reverse);
  const t = el.thickness;
  ctx.beginPath();
  if (el.orientation === 'R') {
    // Top-left to bottom-right, t dots wide horizontally.
    ctx.moveTo(el.x, el.y);
    ctx.lineTo(el.x + t, el.y);
    ctx.lineTo(el.x + el.w, el.y + el.h);
    ctx.lineTo(el.x + el.w - t, el.y + el.h);
  } else {
    ctx.moveTo(el.x + el.w - t, el.y);
    ctx.lineTo(el.x + el.w, el.y);
    ctx.lineTo(el.x + t, el.y + el.h);
    ctx.lineTo(el.x, el.y + el.h);
  }
  ctx.closePath();
  ctx.fill();
  ctx.restore();
}

function drawGraphic(ctx: LabelCanvasContext, el: GraphicElement, reverse: boolean): void {
  if (!el.rowBytes) return;
  ctx.save();
  ctx.fillStyle = setInk(ctx, 'B', reverse);
  const rows = Math.ceil(el.bytes.length / el.rowBytes);
  const width = el.rowBytes * 8;
  for (let r = 0; r < rows; r++) {
    let c = 0;
    while (c < width) {
      const bit = (col: number) => (el.bytes[r * el.rowBytes + (col >> 3)] >> (7 - (col & 7))) & 1;
      if (!bit(c)) {
        c++;
        continue;
      }
      const start = c;
      while (c < width && bit(c)) c++;
      ctx.fillRect(el.x + start, el.y + r, c - start, 1);
    }
  }
  ctx.restore();
}

function drawBarcode(ctx: LabelCanvasContext, el: BarcodeElement, dpi: number, encoder: BarcodeEncoder | null, reverse: boolean, warnings: string[]): void {
  ctx.save();
  const ink = setInk(ctx, 'B', reverse);
  if (!el.data) {
    if (printsEmptySymbol(el.barcodeType)) {
      // Code 128 with empty ^FD (e.g. no SEC yet): the printer still prints the
      // start / check / stop stub, so draw it below like any other symbol.
      warnings.push(emptyBarcodeWarning(el));
    } else {
      warnings.push(emptyBarcodeWarning(el));
      ctx.restore();
      return;
    }
  }
  if (!encoder) {
    enterField(ctx, el.x, el.y, 120, el.h || 60, el.rotate);
    drawBarcodePlaceholder(ctx, 120, el.h || 60, 'barcode');
    ctx.restore();
    return;
  }
  try {
    const problems: string[] = [];
    const enc = encodeBarcode(encoder, el, dpi, problems);
    for (const p of problems) warnings.push(`${el.barcodeType} at ${el.x},${el.y}: ${p}`);
    const size = encodedSize(enc);
    enterField(ctx, el.x, el.y, size.w, size.h, el.rotate);
    drawEncoded(ctx, enc, ink);
  } catch (err) {
    warnings.push(`${el.barcodeType} at ${el.x},${el.y} couldn't be encoded: ${(err as Error).message}`);
    enterField(ctx, el.x, el.y, 120, el.h || 60, el.rotate);
    drawBarcodePlaceholder(ctx, 120, el.h || 60, el.barcodeType);
  }
  ctx.restore();
}

function hasBarcode(doc: LabelDoc): boolean {
  return doc.elements.some((e) => e.type === 'barcode');
}

/**
 * Render one label. The canvas should already be sized (see canvasSizePx());
 * the label is drawn at the top-left. Await document.fonts.ready before calling
 * in a browser so text measures with the real fonts.
 */
export async function renderLabel(ctx: LabelCanvasContext, doc: LabelDoc, options: RenderOptions): Promise<RenderResult> {
  const zoom = options.zoom ?? 1;
  const adjust = options.fontWidthAdjust ?? 1;
  const scale = pxPerDot(options.dpi, zoom);
  const size = labelSizeDots(options);
  const printWidth = doc.meta.widthDots ? Math.min(doc.meta.widthDots, size.width) : size.width;
  const warnings: string[] = [];

  if (doc.meta.widthDots && Math.abs(doc.meta.widthDots - size.width) > size.width * 0.05) {
    warnings.push(
      `^PW is ${doc.meta.widthDots} dots but ${options.widthMm} mm at ${options.dpi} dpi is ${size.width} dots; ` +
        'the template may be for a different resolution',
    );
  }

  const encoder = hasBarcode(doc) ? await loadBarcodeEncoder() : null;
  const measure = canvasMeasure(ctx, adjust);

  ctx.save();
  ctx.scale(scale, scale);
  ctx.imageSmoothingEnabled = false;
  ctx.globalCompositeOperation = 'source-over';
  ctx.fillStyle = WHITE;
  ctx.fillRect(0, 0, size.width, size.height);

  ctx.beginPath();
  ctx.rect(0, 0, printWidth, size.height);
  ctx.clip();
  if (doc.meta.rotate === 180) {
    ctx.translate(size.width, size.height);
    ctx.rotate(Math.PI);
  }

  for (const el of doc.elements) {
    const reverse = doc.meta.reverse || ('reverse' in el && el.reverse);
    drawElement(ctx, el, options.dpi, encoder, measure, adjust, reverse, warnings);
  }
  ctx.restore();

  const extent = measureContent(doc, options, measure);
  for (const c of extent.clipped) warnings.push(`${describeClipping(c)} and will be cut off when printed`);
  for (const c of extent.trimmed) warnings.push(`${describeClipping(c)} (too little to see)`);
  if (extent.overprinted.length) {
    warnings.push(`${extent.overprinted.length} text block(s) don't fit their ^FB line limit; the printer overprints the last line`);
  }
  for (const el of doc.elements) {
    if (el.type === 'text' && isBitmapFont(el.font.name) && el.rotate) {
      warnings.push('Rotated bitmap-font text is approximate');
      break;
    }
  }

  const px = canvasSizePx(options);
  return {
    widthPx: px.width,
    heightPx: px.height,
    extent,
    warnings: [...new Set(warnings)],
    emptyBarcodes: findEmptyBarcodes(doc, options.dpi),
  };
}

function drawElement(
  ctx: LabelCanvasContext,
  el: LabelElement,
  dpi: number,
  encoder: BarcodeEncoder | null,
  measure: MeasureText,
  adjust: number,
  reverse: boolean,
  warnings: string[],
): void {
  switch (el.type) {
    case 'text':
      drawText(ctx, el, measure, adjust, reverse);
      return;
    case 'barcode':
      drawBarcode(ctx, el, dpi, encoder, reverse, warnings);
      return;
    case 'box':
      drawBox(ctx, el, reverse);
      return;
    case 'circle':
      drawCircle(ctx, el, reverse);
      return;
    case 'diagonal':
      drawDiagonal(ctx, el, reverse);
      return;
    case 'graphic':
      drawGraphic(ctx, el, reverse);
      return;
  }
}

